function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
import { useMutation as useApolloMutation } from "@apollo/client";
import { useMemoizedValue } from "@sail/react";
import { useCallback, useMemo } from "react";
import useGetApolloContext from "../../internal/apollo/useGetApolloContext";
import { DEFAULT_IGNORE_TRACKING_FIELDS, DEFAULT_TRACK_ACCESSES_SAMPLE_RATE, useTrackGraphQlFieldAccesses } from "../../internal/deadwood/deadwood";
const onErrorFallback = ()=>{
// Empty function on purpose - otherwise mutations throw (which is not a
// consistent behavior with `useLazyQuery`).
};
/**
 * @see https://sail.stripe.me/data/api/useMutation
 */ export default function useMutation(/**
   * The GraphQL mutation that is going to be executed. This must be created with the
   * [`gql`](/apis/data/gql) or
   * [`graphql`](/apis/data/graphql) template literal.
   */ mutation, /**
   * The default options to pass to the mutation. This argument is optional.
   */ options = {}) {
    const baseContext = useGetApolloContext(options);
    const mutationOptions = useMemo(()=>{
        var _options_onError;
        return _object_spread_props(_object_spread({}, options), {
            onError: (_options_onError = options.onError) !== null && _options_onError !== void 0 ? _options_onError : onErrorFallback,
            context: baseContext
        });
    }, [
        baseContext,
        options
    ]);
    // refetchQueries
    // eslint-disable-next-line react-hooks/exhaustive-deps, react-hooks/immutability
    mutationOptions.refetchQueries = addContextToRefetchQueries(mutationOptions.refetchQueries, baseContext);
    const _useMemo = useMemo(()=>{
        const { variables } = mutationOptions, rest = _object_without_properties(mutationOptions, [
            "variables"
        ]);
        return {
            hookVariables: variables,
            hookOptionsWithoutVariables: rest
        };
    }, [
        mutationOptions
    ]), { hookVariables, hookOptionsWithoutVariables } = _useMemo;
    const memoizedHookVariables = useMemoizedValue(hookVariables);
    const [callback, result] = useApolloMutation(mutation, hookOptionsWithoutVariables);
    const callbackWrapped = useCallback((...restArgs)=>{
        const cbOptions = restArgs[0];
        const mergedVariables = _object_spread({}, memoizedHookVariables, cbOptions === null || cbOptions === void 0 ? void 0 : cbOptions.variables);
        const mergedOptions = _object_spread_props(_object_spread({}, cbOptions), {
            variables: mergedVariables,
            refetchQueries: addContextToRefetchQueries(cbOptions === null || cbOptions === void 0 ? void 0 : cbOptions.refetchQueries, baseContext)
        });
        return callback(mergedOptions).then((_param)=>{
            var { errors } = _param, rest = _object_without_properties(_param, [
                "errors"
            ]);
            return _object_spread_props(_object_spread({}, rest), {
                // Fix for the mistyping of errors in Apollo
                // https://github.com/apollographql/apollo-client/blob/eb2cfee1846b6271e438d1a268e187151e691db4/src/react/hooks/useMutation.ts#L165-L166
                errors: errors,
                error: errors
            });
        });
    }, [
        baseContext,
        callback,
        memoizedHookVariables
    ]);
    var _options_trackAccesses;
    return [
        callbackWrapped,
        useTrackGraphQlFieldAccesses(result, {
            query: mutation,
            sampleRate: (_options_trackAccesses = options.trackAccesses) !== null && _options_trackAccesses !== void 0 ? _options_trackAccesses : DEFAULT_TRACK_ACCESSES_SAMPLE_RATE,
            ignoreFields: DEFAULT_IGNORE_TRACKING_FIELDS
        })
    ];
}
function addContextToRefetchQueries(refetchQueries, context) {
    return refetchQueries === null || refetchQueries === void 0 ? void 0 : refetchQueries.map((queryData)=>{
        if (queryData && typeof queryData === 'object' && 'query' in queryData) {
            return _object_spread_props(_object_spread({}, queryData), {
                context: _object_spread({}, context)
            });
        }
        return queryData;
    });
}

//# sourceMappingURL=useMutation.js.map